import React, { useState } from 'react';
import Modal from '@mui/material/Modal';
import moment from 'moment';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { normalizeExternalUrl } from 'utils/common';
import {
  ModuleContentTooltip,
  moduleTooltipProps,
} from 'components/Common/MUIStyledComponents';
import { Tone } from 'components/ScreeningComposer/ComposerParts';
import { riskToneOf } from 'components/ScreeningComposer/riskTone';
import { useElapsedSeconds } from 'hooks/useClock';
import ViewMore from 'components/ScreeningComposer/ViewMore';
import VerdictBox, {
  BoxJump,
  BoxSkeleton,
  BoxTrack,
} from 'components/ScreeningComposer/VerdictBox';
import type { RiskConfig } from '../searchComponents/AIEvaluate';
import {
  ENTITY_AI_EVAL_NO_STRONG_MATCHES_MESSAGE,
  ENTITY_AI_SAMPLE_SIZE,
} from '../tables/entityTable/entityHitsPagination';

interface EntityVerdictBoxesProps {
  query: string;
  results: any;
  ownershipResults?: any;
  aiSums?: any;
  aiLoading?: boolean;
  entityFilterOptions?: any;
  adverseMedia: any;
  adverseMediaError: string | null;
  adverseMediaLoading: boolean;
  beneficialOwnership: any;
  beneficialOwnershipError: string | null;
  beneficialOwnershipLoading: boolean;
  isInteractionDisabled: boolean;
  riskConfig: RiskConfig | null;
  riskConfigFailed: boolean;
  // The screen on show ran against filters, not every list.
  filtersActive?: boolean;
  unfilteredMatchCount?: number | null;
  onRunAdverseMedia: () => void;
  onRunBeneficialOwnership: () => void;
  onViewAllMatches: (search?: string) => void;
  onViewOwnership: () => void;
}

const riskClass = (value: any): Tone | 'unknown' => {
  const tone = riskToneOf(value);
  return tone === 'none' ? 'unknown' : tone;
};

const stripEntityNameQuotes = (text: string, entityName: string) => {
  if (!text || !entityName) return text || '';
  const escaped = entityName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(
    new RegExp(`['\"“‘’”\\\`]\\s*(${escaped})\\s*['\"“‘’”\\\`]`, 'gi'),
    '$1',
  );
};

const BoxWaiting: React.FC = () => (
  <>
    <BoxTrack />
    <BoxSkeleton lines={4} />
  </>
);

/** The right-hand drawer a box's full evaluation opens in. */
export const BoxDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  titleId: string;
  title: string;
  subtitle: string;
  closeLabel: string;
}> = ({ open, onClose, titleId, title, subtitle, closeLabel, children }) => (
  <Modal open={open} onClose={onClose} hideBackdrop>
    <div className='drawer-backdrop' onClick={onClose}>
      <aside
        className='drawer-panel es2-evaluation-drawer'
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className='drawer-header'>
          <div className='es2-evaluation-heading'>
            <h2 className='drawer-title' id={titleId}>
              {title}
            </h2>
            <p>{subtitle}</p>
          </div>
          <button
            type='button'
            className='drawer-close-btn'
            onClick={onClose}
            aria-label={closeLabel}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </button>
        </div>
        <div className='drawer-body es2-evaluation-body'>{children}</div>
      </aside>
    </div>
  </Modal>
);

const riskTooltipProps = moduleTooltipProps({
  color: 'var(--text-primary-color)',
  border: '1px solid var(--es-soft-border)',
  boxShadow: 'var(--es-card-shadow)',
  maxWidth: '400px',
});

const RiskMatrixTooltip: React.FC<{
  config: RiskConfig | null;
  failed: boolean;
}> = ({ config, failed }) => (
  <div className='es2-risk-tooltip'>
    <strong>How we evaluate risk</strong>
    <p>
      The overall rating follows the strongest sanctions, match and jurisdiction
      signal.
    </p>
    {config ? (
      <>
        <p>
          Matches below{' '}
          <strong>
            {Math.round((config.min_confidence_for_evaluation || 0.7) * 100)}%
          </strong>{' '}
          confidence stay available for manual review but are not sent to the
          evaluator.
        </p>
        <div className='es2-risk-levels'>
          <span className='high'>High</span>
          <span className='medium'>Medium</span>
          <span className='low'>Low</span>
        </div>
      </>
    ) : failed ? (
      <p>The current risk matrix could not be loaded.</p>
    ) : (
      <p>Loading…</p>
    )}
  </div>
);

// where a finding was reported: the publication's own name, else the site it links to
const sourceOf = (finding: any, safeUrl: string | null) => {
  if (finding?.source) return String(finding.source);
  if (!safeUrl) return '';
  try {
    return new URL(safeUrl).hostname.replace(/^www\./, '');
  } catch (_error) {
    return '';
  }
};

/**
 * A web-search check's findings, with the source each was reported in:
 * adverse media by default, and the record page's other risk agents (export
 * control, financial, personnel) under their own title.
 */
export const FindingsDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  entityName: string;
  checkedOn: string;
  adverseMedia: any;
  title?: string;
  /** Overrides the Findings / Clear result, e.g. with an agent's risk level. */
  resultLabel?: string;
  resultTone?: 'high' | 'medium' | 'low' | '';
}> = ({
  open,
  onClose,
  entityName,
  checkedOn,
  adverseMedia,
  title = 'Adverse media evaluation',
  resultLabel,
  resultTone,
}) => {
  const findings = Array.isArray(adverseMedia?.findings)
    ? adverseMedia.findings
    : [];
  const mediaFindings = findings.map((finding: any) => {
    const safeUrl = normalizeExternalUrl(finding?.url) || null;
    return {
      claim: String(finding?.claim || finding?.source || 'Finding'),
      date: finding?.date ? String(finding.date) : '',
      source: sourceOf(finding, safeUrl),
      url: safeUrl,
    };
  });
  const mediaSourceCount = new Set(
    mediaFindings.map((finding: any) => finding.source).filter(Boolean),
  ).size;
  const rawMediaConfidence = adverseMedia?.confidence;
  const mediaConfidence =
    rawMediaConfidence === undefined || rawMediaConfidence === null
      ? NaN
      : Number(rawMediaConfidence);
  const mediaConfidenceLabel = Number.isFinite(mediaConfidence)
    ? `${Math.round(
        mediaConfidence <= 1 ? mediaConfidence * 100 : mediaConfidence,
      )}%`
    : '—';

  return (
    <BoxDrawer
      open={open}
      onClose={onClose}
      titleId='es2-media-title'
      title={title}
      subtitle={`${entityName || 'Screened entity'} · ${checkedOn}`}
      closeLabel={`Close ${title.toLowerCase()}`}
    >
      <div className='es2-evaluation-metrics'>
        <div className='drawer-impact-card'>
          <span className='drawer-impact-label'>Result</span>
          <span
            className={`drawer-impact-value ${
              resultTone !== undefined
                ? resultTone
                : findings.length
                ? 'medium'
                : 'low'
            }`}
          >
            {resultLabel || (findings.length ? 'Findings' : 'Clear')}
          </span>
        </div>
        <div className='drawer-impact-card'>
          <span className='drawer-impact-label'>Findings</span>
          <span className='drawer-impact-value'>
            {findings.length.toLocaleString()}
          </span>
        </div>
        <div className='drawer-impact-card'>
          <span className='drawer-impact-label'>Sources</span>
          <span className='drawer-impact-value'>
            {mediaSourceCount ? mediaSourceCount.toLocaleString() : '—'}
          </span>
        </div>
        <div className='drawer-impact-card'>
          <span className='drawer-impact-label'>Confidence</span>
          <span className='drawer-impact-value'>{mediaConfidenceLabel}</span>
        </div>
      </div>

      <section>
        <div className='drawer-section-title'>Summary</div>
        <p className='drawer-text-block es2-evaluation-summary'>
          {adverseMedia?.summary || 'The check returned no summary.'}
        </p>
      </section>

      <section>
        <div className='drawer-section-title'>Findings</div>
        {mediaFindings.length > 0 ? (
          <ol className='es2-media-findings'>
            {mediaFindings.map((finding: any, index: number) => (
              <li key={`${finding.url || finding.claim}-${index}`}>
                <p>{finding.claim}</p>
                <div className='es2-media-finding-meta'>
                  {finding.source && <span>{finding.source}</span>}
                  {finding.date && <time>{finding.date}</time>}
                  {finding.url && (
                    <a
                      href={finding.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      aria-label={`Open source for finding ${index + 1}${
                        finding.source ? ` (${finding.source})` : ''
                      } in a new tab`}
                    >
                      Open source
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className='drawer-text-block es2-evaluation-empty'>
            Nothing in public sources names {entityName || 'this entity'}.
          </p>
        )}
      </section>
    </BoxDrawer>
  );
};

const EntityVerdictBoxes: React.FC<EntityVerdictBoxesProps> = ({
  query,
  results,
  aiSums,
  aiLoading,
  entityFilterOptions,
  adverseMedia,
  adverseMediaError,
  adverseMediaLoading,
  beneficialOwnership,
  beneficialOwnershipError,
  beneficialOwnershipLoading,
  isInteractionDisabled,
  riskConfig,
  riskConfigFailed,
  filtersActive = false,
  unfilteredMatchCount,
  onRunAdverseMedia,
  onRunBeneficialOwnership,
  onViewAllMatches,
  onViewOwnership,
}) => {
  const [evaluationOpen, setEvaluationOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const summary = aiSums?.[0] || null;
  const total = Number(
    results?.data?.[0]?.result?.body?.hits?.total?.value || 0,
  );
  const aggregations = results?.data?.[0]?.result?.body?.aggregations;
  const lensBuckets = aggregations?.lensAgg?.buckets || [];
  const hitLenses = lensBuckets.filter(
    (bucket: any) => Number(bucket?.doc_count) > 0,
  ).length;
  const availableLenses = entityFilterOptions?.lensOpts?.data?.length || 0;
  const lensCountCopy = filtersActive
    ? typeof unfilteredMatchCount === 'number'
      ? `Filtered: ${total.toLocaleString()} of ${unfilteredMatchCount.toLocaleString()} matches`
      : `Filtered: ${total.toLocaleString()} matches`
    : hitLenses && availableLenses
    ? `${total.toLocaleString()} matches across ${hitLenses} of ${availableLenses} lenses`
    : `${total.toLocaleString()} matches`;
  // A filtered set with nothing to evaluate must never read as a clean screen:
  // the sanctions hit may be one the filters hid.
  const filteredToNothing =
    filtersActive &&
    (total === 0 ||
      summary?.explanation === ENTITY_AI_EVAL_NO_STRONG_MATCHES_MESSAGE);
  const summaryRisk = filteredToNothing
    ? 'No matches in filtered set'
    : summary?.risk || (query ? 'Unknown' : 'N/A');
  const explanation = stripEntityNameQuotes(
    summary?.explanation || '',
    summary?.entity_name || query,
  );
  const countryRisk = summary?.country_risk;
  const countryDataGap = Boolean(countryRisk?.unknown);
  const countryDrove =
    summary?.rating_driver === 'country_tier' ||
    summary?.rating_driver === 'sanctioned_override';
  const topMatchRecords = Array.isArray(summary?.top_matches)
    ? summary.top_matches
        .map((match: any) => ({
          name:
            typeof match === 'string' ? match : match?.name || match?.id || '',
          confidence:
            typeof match === 'string' ? null : Number(match?.confidence),
        }))
        .filter((match: any) => Boolean(match.name))
        .slice(0, 5)
    : [];
  const ownershipElapsed = useElapsedSeconds(beneficialOwnershipLoading);
  const mediaElapsed = useElapsedSeconds(adverseMediaLoading);
  const ownershipDone = Boolean(
    beneficialOwnership?.checked ||
      beneficialOwnership?.summary ||
      beneficialOwnership?.beneficial_ownership_summary,
  );
  const mediaDone = Boolean(adverseMedia);
  const ownershipFound = beneficialOwnership?.ownership_found === true;
  const ownershipCount = ownershipFound
    ? `${Number(beneficialOwnership?.direct_match_count || 0)} direct, ${Number(
        beneficialOwnership?.close_match_count || 0,
      )} close matches`
    : 'No sanctioned owners found';
  const findings = Array.isArray(adverseMedia?.findings)
    ? adverseMedia.findings
    : [];

  const formatConfidence = (value: number | null) => {
    if (value === null || !Number.isFinite(value)) return '';
    const percentage = value <= 1 ? value * 100 : value;
    return `${Math.round(percentage)}% match`;
  };

  const summaryTone = riskToneOf(summaryRisk);
  const mediaTone: Tone = mediaDone
    ? findings.length
      ? 'medium'
      : 'low'
    : 'none';
  const checkedOn = `Checked ${moment().format('MMM D, YYYY')}`;

  return (
    <>
      <div className='psc-boxes'>
        <VerdictBox
          head={{
            glyph: GppMaybeOutlinedIcon,
            glyphTone: aiLoading ? '' : summaryTone,
            label: 'Sanctions & Denied Parties',
            note: 'AI evaluation',
            action: (
              <ModuleContentTooltip
                title={
                  <RiskMatrixTooltip
                    config={riskConfig}
                    failed={riskConfigFailed}
                  />
                }
                placement='bottom-end'
                arrow
                describeChild
                componentsProps={riskTooltipProps}
              >
                <button
                  type='button'
                  className='psc-bh-action psc-bh-action--help'
                >
                  Risk matrix
                </button>
              </ModuleContentTooltip>
            ),
            state: aiLoading ? 'wait' : 'value',
            word: summaryRisk || 'Unknown',
            tone: summaryTone,
            sub: lensCountCopy,
          }}
          foot={
            aiLoading
              ? `Reading the top ${ENTITY_AI_SAMPLE_SIZE} matches`
              : `Top ${ENTITY_AI_SAMPLE_SIZE} evaluated`
          }
          actions={
            aiLoading ? null : (
              <>
                {summary && (
                  <BoxJump
                    onClick={() => setEvaluationOpen(true)}
                    glyph={ChevronRightIcon}
                  >
                    Full evaluation
                  </BoxJump>
                )}
                <BoxJump
                  onClick={() => onViewAllMatches()}
                  glyph={ArrowDownwardIcon}
                >
                  View results
                </BoxJump>
              </>
            )
          }
        >
          {aiLoading ? (
            <BoxWaiting />
          ) : (
            <>
              {!countryDataGap && countryRisk?.is_embargo && countryDrove && (
                <div className='psc-callout psc-callout--high'>
                  <WarningAmberIcon sx={{ fontSize: 15 }} />
                  <span>
                    Comprehensive embargo
                    {countryRisk?.iso ? ` (${countryRisk.iso})` : ''}
                    {' — '}hard stop; escalate to compliance
                  </span>
                </div>
              )}
              {!countryDataGap && !countryRisk?.is_embargo && countryDrove && (
                <div className='psc-callout'>
                  <span>
                    Driven by jurisdiction risk
                    {countryRisk?.iso ? `: ${countryRisk.iso}` : ''}
                    {countryRisk?.category ? ` — ${countryRisk.category}` : ''}
                  </span>
                </div>
              )}
              <ViewMore
                text={
                  explanation ||
                  'Run a screening to generate a contextual risk evaluation for this entity.'
                }
                label='Sanctions and Denied Parties summary'
              />
            </>
          )}
        </VerdictBox>

        <VerdictBox
          head={{
            glyph: PeopleOutlineIcon,
            glyphTone: ownershipDone ? 'none' : '',
            label: 'Beneficial Ownership',
            note: 'sanctions & affiliates',
            state: beneficialOwnershipLoading
              ? 'wait'
              : ownershipDone && !beneficialOwnershipError
              ? 'value'
              : 'flat',
            waitLabel: 'Checking',
            word: beneficialOwnershipError
              ? 'Unavailable'
              : ownershipDone
              ? 'Checked'
              : 'Not run',
            tone: 'none',
            sub: ownershipDone ? ownershipCount : 'No check on file',
          }}
          foot={
            beneficialOwnershipLoading
              ? `${ownershipElapsed}s elapsed`
              : ownershipDone
              ? checkedOn
              : ''
          }
          actions={
            beneficialOwnershipLoading ? null : ownershipDone ? (
              <BoxJump onClick={onViewOwnership} glyph={ArrowDownwardIcon}>
                View results
              </BoxJump>
            ) : (
              <BoxJump
                onClick={onRunBeneficialOwnership}
                disabled={isInteractionDisabled}
                glyph={SearchIcon}
              >
                {beneficialOwnershipError ? 'Retry check' : 'Check ownership'}
              </BoxJump>
            )
          }
        >
          {beneficialOwnershipLoading ? (
            <BoxWaiting />
          ) : (
            <>
              {beneficialOwnershipError && (
                <div className='psc-alert'>{beneficialOwnershipError}</div>
              )}
              <ViewMore
                text={
                  beneficialOwnership?.beneficial_ownership_summary ||
                  beneficialOwnership?.summary ||
                  `Traces owners and affiliates of ${
                    query || 'this entity'
                  } against the 50% rule. It runs on demand, and the result is saved to this record.`
                }
                label='Beneficial Ownership summary'
              />
            </>
          )}
        </VerdictBox>

        <VerdictBox
          head={{
            glyph: ArticleOutlinedIcon,
            glyphTone: mediaDone ? mediaTone : '',
            label: 'Adverse Media',
            note: 'public sources',
            state: adverseMediaLoading
              ? 'wait'
              : mediaDone && !adverseMediaError
              ? 'value'
              : 'flat',
            waitLabel: 'Checking',
            word: adverseMediaError
              ? 'Unavailable'
              : mediaDone
              ? findings.length
                ? 'Findings'
                : 'Clear'
              : 'Not run',
            tone: adverseMediaError ? 'none' : mediaTone,
            sub: mediaDone
              ? `${findings.length} ${
                  findings.length === 1 ? 'finding' : 'findings'
                }`
              : 'No check on file',
          }}
          foot={
            adverseMediaLoading
              ? `${mediaElapsed}s elapsed`
              : mediaDone
              ? checkedOn
              : ''
          }
          actions={
            adverseMediaLoading ? null : (
              <>
                {mediaDone && !adverseMediaError && (
                  <BoxJump
                    onClick={() => setMediaOpen(true)}
                    glyph={ChevronRightIcon}
                    ariaLabel='Full evaluation of adverse media'
                  >
                    Full evaluation
                  </BoxJump>
                )}
                <BoxJump
                  onClick={onRunAdverseMedia}
                  disabled={isInteractionDisabled}
                  glyph={mediaDone ? RefreshIcon : SearchIcon}
                >
                  {adverseMediaError
                    ? 'Retry check'
                    : mediaDone
                    ? 'Re-run'
                    : 'Run check'}
                </BoxJump>
              </>
            )
          }
        >
          {adverseMediaLoading ? (
            <BoxWaiting />
          ) : (
            <>
              {adverseMediaError && (
                <div className='psc-alert'>{adverseMediaError}</div>
              )}
              <ViewMore
                text={
                  adverseMedia?.summary ||
                  `Searches public news, enforcement actions, litigation and ownership reporting for anything naming ${
                    query || 'this entity'
                  }. It runs on demand because it queries outside BITE, and the result is saved to this record.`
                }
                label='Adverse Media summary'
              />
            </>
          )}
        </VerdictBox>
      </div>

      <BoxDrawer
        open={evaluationOpen}
        onClose={() => setEvaluationOpen(false)}
        titleId='es2-evaluation-title'
        title='Full evaluation'
        subtitle={`${query || summary?.entity_name || 'Screened entity'} · ${
          summary?.screenDate || 'Latest screen'
        }`}
        closeLabel='Close full evaluation'
      >
        <div className='es2-evaluation-metrics'>
          <div className='drawer-impact-card'>
            <span className='drawer-impact-label'>Risk</span>
            <span className={`drawer-impact-value ${riskClass(summaryRisk)}`}>
              {summaryRisk || 'Unknown'}
            </span>
          </div>
          <div className='drawer-impact-card'>
            <span className='drawer-impact-label'>Matches</span>
            <span className='drawer-impact-value'>
              {total.toLocaleString()}
            </span>
          </div>
          <div className='drawer-impact-card'>
            <span className='drawer-impact-label'>Lenses matched</span>
            <span className='drawer-impact-value'>
              {availableLenses
                ? `${hitLenses} of ${availableLenses}`
                : hitLenses}
            </span>
          </div>
          <div className='drawer-impact-card'>
            <span className='drawer-impact-label'>Best-match confidence</span>
            <span className='drawer-impact-value'>
              {formatConfidence(Number(summary?.match_probability)) || '—'}
            </span>
          </div>
        </div>

        <section>
          <div className='drawer-section-title'>Evaluation summary</div>
          <p className='drawer-text-block es2-evaluation-summary'>
            {explanation || 'The evaluation returned no explanation.'}
          </p>
        </section>

        {countryRisk && (
          <section>
            <div className='drawer-section-title'>Jurisdiction assessment</div>
            <div className='drawer-kv-box'>
              <div className='drawer-kv-row'>
                <span className='drawer-kv-label'>Jurisdiction</span>
                <span className='drawer-kv-value'>
                  {countryRisk.iso || 'Not identified'}
                </span>
              </div>
              <div className='drawer-kv-row'>
                <span className='drawer-kv-label'>Category</span>
                <span className='drawer-kv-value'>
                  {countryRisk.category || countryRisk.tier || 'Unknown'}
                </span>
              </div>
              <div className='drawer-kv-row'>
                <span className='drawer-kv-label'>Rating driver</span>
                <span className='drawer-kv-value'>
                  {summary?.rating_driver || 'Match evidence'}
                </span>
              </div>
              <div className='drawer-kv-row'>
                <span className='drawer-kv-label'>Sanctions / embargo</span>
                <span className='drawer-kv-value'>
                  {countryRisk.is_sanctioned || countryRisk.is_embargo
                    ? 'Flagged'
                    : 'No jurisdiction flag'}
                </span>
              </div>
            </div>
          </section>
        )}

        <section>
          <div className='drawer-section-title'>Top matches</div>
          {topMatchRecords.length > 0 ? (
            <div className='es2-evaluation-top-matches'>
              {topMatchRecords.map((match: any) => (
                <button
                  key={match.name}
                  type='button'
                  onClick={() => {
                    setEvaluationOpen(false);
                    onViewAllMatches(match.name);
                  }}
                >
                  <span>{match.name}</span>
                  <em>{formatConfidence(match.confidence)}</em>
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </button>
              ))}
            </div>
          ) : (
            <p className='drawer-text-block es2-evaluation-empty'>
              No individual top matches were returned.
            </p>
          )}
        </section>
      </BoxDrawer>

      <FindingsDrawer
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        entityName={query}
        checkedOn={checkedOn}
        adverseMedia={adverseMedia}
      />
    </>
  );
};

export default EntityVerdictBoxes;
